<template>
  <b-modal ref="historyModal" title="Lịch sử ngân sách" size="xl" hide-footer>
    <b-table
      v-if="histories && histories.length"
      :items="histories"
      :fields="historyFields"
      bordered
      hover
      small
    >
      <template #cell(createdAt)="data">
        {{ formatDateTime(data.value) }}
      </template>
      <template #cell(description)="data">
        <div>{{ data.value }}</div>
        <div v-if="data.item.newValue && data.item.newValue.budgetLineRequestId" class="mt-1">
          <router-link
            :to="{ name: 'detail-budget-line-request', query: { id: data.item.newValue.budgetLineRequestId } }"
            class="small text-primary"
          >
            Xem yêu cầu #{{ data.item.newValue.budgetLineRequestId }}
          </router-link>
        </div>
      </template>
      <template #cell(amountChange)="data">
        <!-- Xem theo tháng: có allocatedAmount/remainingAmount -->
        <template v-if="hasDetailAmountChange(data.item)">
          <div class="small">
            <div v-if="data.item.newValue && data.item.newValue.period" class="mb-1">
              <span class="text-muted">Kỳ: </span>
              <strong>{{ formatPeriod(data.item.newValue.period) }}</strong>
            </div>
            <div>
              <span class="text-muted">NS phân bổ: </span>
              <span>{{ formatAmount(data.item.originalValue && data.item.originalValue.allocatedAmount) }}</span>
              <i class="fas fa-arrow-right mx-1 text-muted" style="font-size:10px"></i>
              <strong class="text-success">{{ formatAmount(data.item.newValue && data.item.newValue.allocatedAmount) }}</strong>
            </div>
            <div class="mt-1">
              <span class="text-muted">NS còn lại: </span>
              <span>{{ formatAmount(data.item.originalValue && data.item.originalValue.remainingAmount) }}</span>
              <i class="fas fa-arrow-right mx-1 text-muted" style="font-size:10px"></i>
              <strong class="text-success">{{ formatAmount(data.item.newValue && data.item.newValue.remainingAmount) }}</strong>
            </div>
          </div>
        </template>
        <!-- PR commit/rollback/reconcile: thay đổi usedAmount + remainingAmount -->
        <template v-else-if="hasUsedAmountChange(data.item)">
          <div class="small">
            <div>
              <span class="text-muted">NS đã dùng: </span>
              <span>{{ formatAmount(data.item.originalValue && data.item.originalValue.usedAmount) }}</span>
              <i class="fas fa-arrow-right mx-1 text-muted" style="font-size:10px"></i>
              <strong :class="isUsedAmountIncreased(data.item) ? 'text-danger' : 'text-success'">
                {{ formatAmount(data.item.newValue && data.item.newValue.usedAmount) }}
              </strong>
            </div>
            <div class="mt-1">
              <span class="text-muted">NS còn lại: </span>
              <span>{{ formatAmount(data.item.originalValue && data.item.originalValue.remainingAmount) }}</span>
              <i class="fas fa-arrow-right mx-1 text-muted" style="font-size:10px"></i>
              <strong :class="isUsedAmountIncreased(data.item) ? 'text-success' : 'text-danger'">
                {{ formatAmount(data.item.newValue && data.item.newValue.remainingAmount) }}
              </strong>
            </div>
          </div>
        </template>
        <!-- Xem theo năm (parent): chỉ có requestedAmount của tháng con -->
        <template v-else-if="hasRequestedAmountChange(data.item)">
          <div class="small">
            <div v-if="data.item.newValue && data.item.newValue.period" class="mb-1">
              <span class="text-muted">Kỳ: </span>
              <strong>{{ formatPeriod(data.item.newValue.period) }}</strong>
            </div>
            <div>
              <span class="text-muted">Số tiền bổ sung: </span>
              <strong :class="isRollback(data.item) ? 'text-danger' : 'text-success'">
                {{ isRollback(data.item) ? '-' : '+' }}{{ formatAmount(data.item.newValue.requestedAmount) }}
              </strong>
            </div>
          </div>
        </template>
        <template v-else>
          <span class="text-muted">—</span>
        </template>
      </template>
      <template #cell(updatedBy)="data">
        {{ data.item.updatedByName || data.value }}
      </template>
    </b-table>

    <p v-else class="text-muted mb-0">Chưa có lịch sử.</p>
  </b-modal>
</template>

<script>
import moment from 'moment';
import { convertPrice } from '@/utils/common';

export default {
  name: 'BudgetLineHistoryModal',
  props: {
    histories: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    historyFields() {
      return [
        { key: 'createdAt', label: 'Lúc', thStyle: { width: '140px' } },
        { key: 'actionLabel', label: 'Hành động', thStyle: { width: '120px' } },
        { key: 'description', label: 'Chi tiết' },
        { key: 'amountChange', label: 'Thay đổi ngân sách', thStyle: { width: '260px' } },
        { key: 'updatedBy', label: 'Người thực hiện', thStyle: { width: '140px' }, tdClass: 'text-center' },
      ];
    },
  },
  methods: {
    open() {
      this.$refs.historyModal.show();
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '';
    },
    formatPeriod(val) {
      return val ? moment(val).format('MM/YYYY') : '';
    },
    formatAmount(val) {
      return val != null ? `${convertPrice(val)} đ` : '—';
    },
    hasDetailAmountChange(item) {
      return (
        (item.originalValue && item.originalValue.allocatedAmount != null) ||
        (item.newValue && item.newValue.allocatedAmount != null)
      );
    },
    hasRequestedAmountChange(item) {
      return item.newValue && item.newValue.requestedAmount != null;
    },
    hasUsedAmountChange(item) {
      return (
        (item.originalValue && item.originalValue.usedAmount != null) ||
        (item.newValue && item.newValue.usedAmount != null)
      );
    },
    isUsedAmountIncreased(item) {
      const orig = item.originalValue ? Number(item.originalValue.usedAmount) || 0 : 0;
      const next = item.newValue ? Number(item.newValue.usedAmount) || 0 : 0;
      return next > orig;
    },
    isRollback(item) {
      return item.description && item.description.includes('Hoàn duyệt');
    },
  },
};
</script>

